import {
  BookOpen,
  Boxes,
  FileText,
  Key,
  MessageSquarePlus,
  Plug,
  Rocket,
  Sparkles,
  Workflow,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { usePermissions } from "@/hooks/usePermissions";

export type NavItem = {
  key: string;
  label: string;
  to: string;
  icon: LucideIcon;
  visible: boolean;
};

export type NavSection = {
  key: string;
  /** Sections without a label render as a bare list, per the design. */
  label?: string;
  collapsible: boolean;
  items: NavItem[];
};

/**
 * The navigation contract, carried over from the old DashboardLayout.
 *
 * The visibility rules are the part worth preserving verbatim — they are the only
 * thing standing between an inference-only org and pages it must not see. The
 * grouping and labels are new, from the redesign.
 *
 * Note `/mcps` and `/blueprints` in lowercase. The old layout linked to "/MCPS",
 * and the legacy tree declared Blueprints as "/bluePrints" while linking to
 * "/blueprints". Both worked because react-router v6 matches case-insensitively —
 * and both still work, because TanStack Router does too (`caseSensitive` defaults
 * to false; seam.test.ts pins it). Canonical lowercase here is hygiene, not a fix
 * for a breakage: one spelling per screen, so the active-state check has something
 * stable to compare against.
 */
export const useNavSections = (): NavSection[] => {
  const { t } = useTranslation();
  const { canViewDeployments, canViewApiKey, isInferenceOnly } =
    usePermissions();

  const sections: NavSection[] = [
    {
      key: "primary",
      collapsible: false,
      items: [
        {
          key: "newChat",
          label: t("sidebar.newChat"),
          to: "/playground",
          icon: MessageSquarePlus,
          visible: true,
        },
        {
          key: "blueprints",
          label: t("sidebar.bluePrints"),
          to: "/blueprints",
          icon: Sparkles,
          visible: !isInferenceOnly,
        },
        {
          key: "apiKeys",
          label: t("sidebar.apiKeys"),
          to: "/platform/security/api-keys",
          icon: Key,
          visible: canViewApiKey,
        },
        {
          key: "usageCost",
          label: t("sidebar.usageCost"),
          to: "/usage",
          icon: FileText,
          visible: true,
        },
      ],
    },
    {
      key: "knowledgeBase",
      label: t("sidebar.knowledgeBase"),
      collapsible: true,
      items: [
        {
          key: "collections",
          label: t("sidebar.documentCollections"),
          to: "/collections",
          icon: BookOpen,
          visible: !isInferenceOnly,
        },
        {
          key: "connectors",
          label: t("sidebar.connectors"),
          to: "/mcps",
          icon: Plug,
          visible: !isInferenceOnly,
        },
        {
          key: "flows",
          label: t("sidebar.flows"),
          to: "/flows",
          icon: Workflow,
          visible: !isInferenceOnly,
        },
        {
          key: "aiCatalog",
          label: t("sidebar.aiCatalog"),
          to: "/ai-catalog",
          icon: Boxes,
          visible: true,
        },
        {
          key: "deployments",
          label: t("sidebar.deployments"),
          to: "/deployments",
          icon: Rocket,
          visible: canViewDeployments,
        },
      ],
    },
  ];

  return sections
    .map((s) => ({ ...s, items: s.items.filter((i) => i.visible) }))
    .filter((s) => s.items.length > 0);
};
